import { QrCode } from "lucide-react";

export function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white py-10 px-4">
      <div className="max-w-3xl mx-auto space-y-8">
        <header className="space-y-3">
          <div className="inline-flex items-center gap-2 rounded-full bg-blue-100 px-3 py-1 text-xs font-medium text-blue-800">
            <QrCode className="h-3 w-3" />
            <span>Conciergie · Privacy Policy</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-semibold text-gray-900">
            Privacy Policy
          </h1>
          <p className="text-sm text-gray-500">
            This Privacy Policy explains how we collect and use information when you
            use Conciergie. It is designed to be consistent with the core
            transparency requirements of the EU General Data Protection Regulation
            (GDPR) and similar global privacy standards.
          </p>
          <p className="text-xs text-gray-500">
            This text is provided for product transparency only and does not
            constitute legal advice. Please review it with your legal counsel to
            ensure it fits your specific use case.
          </p>
        </header>

        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-gray-900">1. Who is responsible for your data?</h2>
          <p className="text-sm text-gray-700">
            The data controller responsible for the processing of your personal data
            in connection with Conciergie is:
          </p>
          <p className="text-sm text-gray-700">
            <span className="font-medium">Conciergie</span>
            <br />
            Email: <a href="mailto:privacy@conciergie.com" className="underline underline-offset-2">
              privacy@conciergie.com
            </a>
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-gray-900">2. What information do we collect?</h2>
          <p className="text-sm text-gray-700">
            When you use Conciergie, we may process the following categories of information:
          </p>
          <ul className="list-disc space-y-2 pl-6 text-sm text-gray-700">
            <li>
              <span className="font-medium">Technical and usage data</span>, such as
              anonymised or pseudonymised analytics about how you use the website
              (pages viewed, device type, approximate region, and similar events).
            </li>
            <li>
              <span className="font-medium">Contact data</span>, such as your email
              address if you register for full access or sign up to hear more about
              Conciergie.
            </li>
            <li>
              <span className="font-medium">Service configuration data</span>, such
              as internal identifiers (for example, hotel or QR code IDs) that allow
              us to associate generated QR codes with your account or property.
            </li>
          </ul>
          <p className="text-sm text-gray-700">
            Conciergie is designed for uploading hotel directories and similar
            materials that should not contain personal data about guests. You are
            responsible for ensuring that documents you upload do not contain
            personal data. If such data is uploaded by mistake, we will process it
            only as necessary to provide the service and will delete it in
            accordance with this policy.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-gray-900">3. For what purposes and on what legal bases do we use your data?</h2>
          <p className="text-sm text-gray-700">
            We process personal data only where we have a valid legal basis under
            GDPR and similar laws. Depending on how you use Conciergie, this may include:
          </p>
          <ul className="list-disc space-y-2 pl-6 text-sm text-gray-700">
            <li>
              <span className="font-medium">Providing and improving the service</span>{" "}
              (Article 6(1)(b) and 6(1)(f) GDPR): we process technical and
              configuration data to operate, secure, and improve Conciergie, and to
              generate and maintain QR codes and related features.
            </li>
            <li>
              <span className="font-medium">Communicating with you</span>{" "}
              (Article 6(1)(b) and, where required, 6(1)(a) GDPR): if you provide
              your email address, we use it to send you transactional messages (such
              as verification emails) and, where you request it, information about
              product updates and features.
            </li>
            <li>
              <span className="font-medium">Analytics and product insights</span>{" "}
              (Article 6(1)(f) GDPR): we use privacy-friendly analytics to understand
              how Conciergie is used so that we can maintain performance, security,
              and a high-quality user experience. Wherever possible, analytics data
              is aggregated or pseudonymised.
            </li>
            <li>
              <span className="font-medium">Legal compliance and protection</span>{" "}
              (Article 6(1)(c) and 6(1)(f) GDPR): we may process limited data to
              comply with legal obligations, enforce our terms, or protect our
              rights, users, or the public.
            </li>
          </ul>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-gray-900">4. Cookies and similar technologies</h2>
          <p className="text-sm text-gray-700">
            Conciergie uses cookies and similar technologies that are necessary to
            operate the website and provide core features. We may also use
            privacy-friendly analytics tools that rely on cookies or similar
            identifiers to understand usage patterns.
          </p>
          <p className="text-sm text-gray-700">
            Where required by applicable law, we will obtain your consent before
            setting non-essential cookies or similar technologies. You can manage
            your browser settings to delete or block cookies, although this may
            affect the functionality of the service.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-gray-900">5. How long do we keep your data?</h2>
          <p className="text-sm text-gray-700">
            We retain personal data only for as long as necessary for the purposes
            described in this policy:
          </p>
          <ul className="list-disc space-y-2 pl-6 text-sm text-gray-700">
            <li>
              Contact data is kept while we have an ongoing relationship with you
              (for example, while you maintain an account or are subscribed to
              updates) and for a reasonable period afterwards to respond to queries,
              maintain records, or comply with legal obligations.
            </li>
            <li>
              Analytics data is generally aggregated or pseudonymised and retained
              for a limited period to allow us to analyse trends and improve the
              service.
            </li>
            <li>
              Uploaded documents and related configuration data are kept for as long
              as needed to operate Conciergie for your organisation, unless you
              request deletion earlier where applicable.
            </li>
          </ul>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-gray-900">6. Who do we share data with?</h2>
          <p className="text-sm text-gray-700">
            We do not sell your personal data. We may share limited information with:
          </p>
          <ul className="list-disc space-y-2 pl-6 text-sm text-gray-700">
            <li>
              <span className="font-medium">Service providers (processors)</span>{" "}
              who host our infrastructure, send emails, or provide analytics and
              similar services. These providers are contractually required to
              process personal data only on our instructions and with appropriate
              security safeguards.
            </li>
            <li>
              <span className="font-medium">Professional advisers and authorities</span>{" "}
              where necessary to comply with the law, enforce our rights, or respond
              to lawful requests.
            </li>
          </ul>
          <p className="text-sm text-gray-700">
            Some of these providers may be located outside your country, including
            outside the European Economic Area (EEA). Where this involves a transfer
            of personal data from the EEA or United Kingdom, we will implement
            appropriate safeguards, such as the EU Standard Contractual Clauses or
            equivalent mechanisms, as required by law.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-gray-900">7. Your rights under GDPR</h2>
          <p className="text-sm text-gray-700">
            If you are located in the European Economic Area, United Kingdom, or
            another region with similar data protection laws, you have certain
            rights in relation to your personal data. Subject to applicable law and
            certain limitations, these may include:
          </p>
          <ul className="list-disc space-y-2 pl-6 text-sm text-gray-700">
            <li>Right of access to your personal data.</li>
            <li>Right to rectification of inaccurate or incomplete data.</li>
            <li>Right to erasure ("right to be forgotten").</li>
            <li>Right to restriction of processing.</li>
            <li>Right to data portability.</li>
            <li>
              Right to object to processing based on our legitimate interests or for
              direct marketing purposes.
            </li>
            <li>
              Right to withdraw consent at any time, where processing is based on
              your consent. This will not affect the lawfulness of processing before
              withdrawal.
            </li>
          </ul>
          <p className="text-sm text-gray-700">
            You also have the right to lodge a complaint with your local data
            protection authority if you believe your rights have been violated. We
            would, however, appreciate the chance to address your concerns first.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-gray-900">8. How to contact us about privacy</h2>
          <p className="text-sm text-gray-700">
            If you have questions about this Privacy Policy or wish to exercise your
            data protection rights, you can contact us at:
          </p>
          <p className="text-sm text-gray-700">
            Email:{" "}
            <a href="mailto:privacy@conciergie.com" className="underline underline-offset-2">
              privacy@conciergie.com
            </a>
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-gray-900">9. Changes to this Privacy Policy</h2>
          <p className="text-sm text-gray-700">
            We may update this Privacy Policy from time to time to reflect changes
            in our service or legal requirements. When we make material changes, we
            will update the &quot;last updated&quot; date and, where appropriate,
            provide additional notice.
          </p>
          <p className="text-xs text-gray-500">Last updated: {new Date().toISOString().slice(0, 10)}</p>
        </section>
      </div>
    </div>
  );
}


